import { inject, Injectable } from '@angular/core';
import { ApiRequestService, Void } from '@prekog/common-ui';
import { catchError, EMPTY, Observable } from 'rxjs';

export type RecalculationRequest = {
	productIds: number[];
};

export type ProductEventRecalculationInput = {
	productId: number;
	eventDate: string;
};

@Injectable({ providedIn: 'root' })
export class RecalculationService {
	private _apiRequestService = inject(ApiRequestService);
	private _storeBaseUrl = inject<string>('STORE_BACKEND_URL' as never);
	private _recalculateUri = '/admin/recalculateProductStatusSnapshot';

	buildBatchRecalculationRequest(events: ProductEventRecalculationInput[]): RecalculationRequest | null {
		const today = this._buildTodayString();
		const productIdsWithPastEvents = new Set<number>();
		for (const event of events) {
			if (event.eventDate < today) {
				productIdsWithPastEvents.add(event.productId);
			}
		}
		if (productIdsWithPastEvents.size === 0) {
			return null;
		}
		return { productIds: [...productIdsWithPastEvents] };
	}

	buildEditRecalculationRequest(
		productId: number,
		existingEventDate: string,
		newEventDate: string
	): RecalculationRequest | null {
		const today = this._buildTodayString();
		const earliestDate = this._earliest(existingEventDate, newEventDate);
		if (earliestDate >= today) {
			return null;
		}
		return { productIds: [productId] };
	}

	buildDeleteRecalculationRequest(productId: number, eventDate: string): RecalculationRequest | null {
		const today = this._buildTodayString();
		if (eventDate >= today) {
			return null;
		}
		return { productIds: [productId] };
	}

	dispatchRecalculation(request: RecalculationRequest): void {
		this._apiRequestService
			.sendRequest<RecalculationRequest, Void>(this._storeBaseUrl, request, true, this._recalculateUri)
			.pipe(catchError(() => EMPTY))
			.subscribe();
	}

	sendRecalculationRequest(request: RecalculationRequest): Observable<Void> {
		return this._apiRequestService.sendRequest<RecalculationRequest, Void>(
			this._storeBaseUrl,
			request,
			true,
			this._recalculateUri
		);
	}

	private _buildTodayString(): string {
		const now = new Date();
		return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
	}

	private _earliest(a: string, b: string): string {
		return a <= b ? a : b;
	}
}
